import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const TradeBalanceTreemap = () => {
  const { isDarkMode } = useTheme();
  // Main control state - we'll now show all modes simultaneously
  const modes = ['Import', 'Export', 'Import Dependency'];

  // Region color mappings based on COUNTRY_MAPPINGS_ACTUAL
  const regionColors = {
    'Asia': '#00847E',           // Blue #3B82F6
    'Africa': '#A2559C',         // Amber #F59E0B
    'Europe': '#4C6A9C',         // Green #10B981
    'America': '#E56E5A',        // Red #EF4444
    'Australia and Oceania': '#38AABA', // Purple
    'Antarctica': '#883039'      // Gray
  };

  // Country to region mapping based on COUNTRY_MAPPINGS_ACTUAL from enhanced_geo_config.py
  const countryRegionMapping = {
    // Asia
    'Kazakhstan': 'Asia', 'Kyrgyzstan': 'Asia', 'Tajikistan': 'Asia', 'Turkmenistan': 'Asia', 'Uzbekistan': 'Asia',
    'China': 'Asia', 'Hong Kong Special Administrative Region': 'Asia', 'Macao Special Administrative Region': 'Asia',
    'China Taiwan': 'Asia', 'Democratic People\'s Republic of Korea': 'Asia', 'Japan': 'Asia', 'Mongolia': 'Asia',
    'Republic of Korea': 'Asia', 'South Korea': 'Asia', 'Taiwan': 'Asia', 'North Korea': 'Asia',
    'Brunei Darussalam': 'Asia', 'Cambodia': 'Asia', 'Indonesia': 'Asia', 'Lao People\'s Democratic Republic': 'Asia',
    'Malaysia': 'Asia', 'Myanmar': 'Asia', 'Philippines': 'Asia', 'Singapore': 'Asia', 'Thailand': 'Asia',
    'Timor-Leste': 'Asia', 'Viet Nam': 'Asia', 'Vietnam': 'Asia', 'Laos': 'Asia',
    'Afghanistan': 'Asia', 'Bangladesh': 'Asia', 'Bhutan': 'Asia', 'India': 'Asia', 'Maldives': 'Asia',
    'Nepal': 'Asia', 'Pakistan': 'Asia', 'Sri Lanka': 'Asia',
    'Armenia': 'Asia', 'Azerbaijan': 'Asia', 'Bahrain': 'Asia', 'Cyprus': 'Asia', 'Georgia': 'Asia',
    'Iraq': 'Asia', 'Iran (Islamic Republic of)': 'Asia', 'Iran': 'Asia', 'Israel': 'Asia', 'Jordan': 'Asia',
    'Kuwait': 'Asia', 'Lebanon': 'Asia', 'Oman': 'Asia', 'Qatar': 'Asia', 'Saudi Arabia': 'Asia',
    'State of Palestine': 'Asia', 'Palestine': 'Asia', 'Syrian Arab Republic': 'Asia', 'Syria': 'Asia',
    'Türkiye': 'Asia', 'Turkey': 'Asia', 'United Arab Emirates': 'Asia', 'UAE': 'Asia', 'Yemen': 'Asia',
    
    // Africa
    'Algeria': 'Africa', 'Egypt': 'Africa', 'Libya': 'Africa', 'Morocco': 'Africa', 'Sudan': 'Africa',
    'Tunisia': 'Africa', 'Western Sahara': 'Africa',
    'British Indian Ocean Territory': 'Africa', 'Burundi': 'Africa', 'Comoros': 'Africa', 'Djibouti': 'Africa',
    'Eritrea': 'Africa', 'Ethiopia': 'Africa', 'French Southern Territories': 'Africa', 'Kenya': 'Africa',
    'Madagascar': 'Africa', 'Malawi': 'Africa', 'Mauritius': 'Africa', 'Mayotte': 'Africa', 'Mozambique': 'Africa',
    'Réunion': 'Africa', 'Rwanda': 'Africa', 'Seychelles': 'Africa', 'Somalia': 'Africa', 'South Sudan': 'Africa',
    'Uganda': 'Africa', 'United Republic of Tanzania': 'Africa', 'Tanzania': 'Africa', 'Zambia': 'Africa',
    'Zimbabwe': 'Africa', 'Angola': 'Africa', 'Cameroon': 'Africa', 'Central African Republic': 'Africa',
    'Chad': 'Africa', 'Congo': 'Africa', 'Democratic Republic of the Congo': 'Africa', 'DRC': 'Africa',
    'Equatorial Guinea': 'Africa', 'Gabon': 'Africa', 'Sao Tome and Principe': 'Africa', 'Botswana': 'Africa',
    'Eswatini': 'Africa', 'Lesotho': 'Africa', 'Namibia': 'Africa', 'South Africa': 'Africa', 'Benin': 'Africa',
    'Burkina Faso': 'Africa', 'Cabo Verde': 'Africa', 'Côte d\'Ivoire': 'Africa', 'Ivory Coast': 'Africa',
    'Gambia': 'Africa', 'Ghana': 'Africa', 'Guinea': 'Africa', 'Guinea-Bissau': 'Africa', 'Liberia': 'Africa',
    'Mali': 'Africa', 'Mauritania': 'Africa', 'Niger': 'Africa', 'Nigeria': 'Africa', 'Saint Helena': 'Africa',
    'Senegal': 'Africa', 'Sierra Leone': 'Africa', 'Togo': 'Africa',
    
    // Europe
    'Belarus': 'Europe', 'Bulgaria': 'Europe', 'Czechia': 'Europe', 'Czech Republic': 'Europe', 'Hungary': 'Europe',
    'Poland': 'Europe', 'Republic of Moldova': 'Europe', 'Moldova': 'Europe', 'Romania': 'Europe',
    'Russian Federation': 'Europe', 'Russia': 'Europe', 'Slovakia': 'Europe', 'Ukraine': 'Europe',
    'Åland Islands': 'Europe', 'Denmark': 'Europe', 'Estonia': 'Europe', 'Faroe Islands': 'Europe',
    'Finland': 'Europe', 'Guernsey': 'Europe', 'Iceland': 'Europe', 'Ireland': 'Europe', 'Isle of Man': 'Europe',
    'Jersey': 'Europe', 'Latvia': 'Europe', 'Lithuania': 'Europe', 'Norway': 'Europe',
    'Svalbard and Jan Mayen Islands': 'Europe', 'Sweden': 'Europe', 'United Kingdom of Great Britain and Northern Ireland': 'Europe',
    'United Kingdom': 'Europe', 'UK': 'Europe', 'Great Britain': 'Europe',
    'Albania': 'Europe', 'Andorra': 'Europe', 'Bosnia and Herzegovina': 'Europe', 'Croatia': 'Europe',
    'Gibraltar': 'Europe', 'Greece': 'Europe', 'Holy See': 'Europe', 'Italy': 'Europe', 'Malta': 'Europe',
    'Montenegro': 'Europe', 'North Macedonia': 'Europe', 'Macedonia': 'Europe', 'Portugal': 'Europe',
    'San Marino': 'Europe', 'Serbia': 'Europe', 'Slovenia': 'Europe', 'Spain': 'Europe',
    'Austria': 'Europe', 'Belgium': 'Europe', 'France': 'Europe', 'Germany': 'Europe', 'Liechtenstein': 'Europe',
    'Luxembourg': 'Europe', 'Monaco': 'Europe', 'Netherlands (Kingdom of the)': 'Europe', 'Netherlands': 'Europe',
    'Switzerland': 'Europe',
    
    // America
    'Bermuda': 'America', 'Canada': 'America', 'Greenland': 'America', 'Saint Pierre and Miquelon': 'America',
    'United States of America': 'America', 'United States': 'America', 'USA': 'America', 'US': 'America',
    'Anguilla': 'America', 'Antigua and Barbuda': 'America', 'Aruba': 'America', 'Bahamas': 'America',
    'Barbados': 'America', 'Bonaire, Sint Eustatius and Saba': 'America', 'British Virgin Islands': 'America',
    'Cayman Islands': 'America', 'Cuba': 'America', 'Curaçao': 'America', 'Dominica': 'America',
    'Dominican Republic': 'America', 'Grenada': 'America', 'Guadeloupe': 'America', 'Haiti': 'America',
    'Jamaica': 'America', 'Martinique': 'America', 'Montserrat': 'America', 'Puerto Rico': 'America',
    'Saint Barthélemy': 'America', 'Saint Kitts and Nevis': 'America', 'Saint Lucia': 'America',
    'Saint Martin (French Part)': 'America', 'Saint Vincent and the Grenadines': 'America',
    'Sint Maarten (Dutch part)': 'America', 'Trinidad and Tobago': 'America', 'Turks and Caicos Islands': 'America',
    'United States Virgin Islands': 'America', 'Belize': 'America', 'Costa Rica': 'America', 'El Salvador': 'America',
    'Guatemala': 'America', 'Honduras': 'America', 'Mexico': 'America', 'Nicaragua': 'America', 'Panama': 'America',
    'Argentina': 'America', 'Bolivia (Plurinational State of)': 'America', 'Bolivia': 'America',
    'Bouvet Island': 'America', 'Brazil': 'America', 'Chile': 'America', 'Colombia': 'America', 'Ecuador': 'America',
    'Falkland Islands (Malvinas)': 'America', 'French Guiana': 'America', 'Guyana': 'America', 'Paraguay': 'America',
    'Peru': 'America', 'South Georgia and the South Sandwich Islands': 'America', 'Suriname': 'America',
    'Uruguay': 'America', 'Venezuela (Bolivarian Republic of)': 'America', 'Venezuela': 'America',
    
    // Australia and Oceania
    'Australia': 'Australia and Oceania', 'Christmas Island': 'Australia and Oceania',
    'Cocos (Keeling) Islands': 'Australia and Oceania', 'Heard Island and McDonald Islands': 'Australia and Oceania',
    'New Zealand': 'Australia and Oceania', 'Norfolk Island': 'Australia and Oceania',
    'Fiji': 'Australia and Oceania', 'New Caledonia': 'Australia and Oceania', 'Papua New Guinea': 'Australia and Oceania',
    'Solomon Islands': 'Australia and Oceania', 'Vanuatu': 'Australia and Oceania', 'Guam': 'Australia and Oceania',
    'Kiribati': 'Australia and Oceania', 'Marshall Islands': 'Australia and Oceania',
    'Micronesia (Federated States of)': 'Australia and Oceania', 'Micronesia': 'Australia and Oceania',
    'Nauru': 'Australia and Oceania', 'Northern Mariana Islands': 'Australia and Oceania', 'Palau': 'Australia and Oceania',
    'United States Minor Outlying Islands': 'Australia and Oceania', 'American Samoa': 'Australia and Oceania',
    'Cook Islands': 'Australia and Oceania', 'French Polynesia': 'Australia and Oceania', 'Niue': 'Australia and Oceania',
    'Pitcairn': 'Australia and Oceania', 'Samoa': 'Australia and Oceania', 'Tokelau': 'Australia and Oceania',
    'Tonga': 'Australia and Oceania', 'Tuvalu': 'Australia and Oceania', 'Wallis and Futuna Islands': 'Australia and Oceania',
    
    // Antarctica
    'Antarctica': 'Antarctica'
  };

  // Helper function to get region for a country
  const getCountryRegion = (countryName) => {
    if (!countryName) return null;
    return countryRegionMapping[countryName] || null;
  };

  // Helper function to get region color
  const getRegionColor = (countryName) => {
    const region = getCountryRegion(countryName);
    return region ? regionColors[region] : '#6B7280'; // Default gray for unknown regions
  };
  
  // Import/Export states
  const [selectedMetric, setSelectedMetric] = useState('Trade Value');
  const [selectedUnit, setSelectedUnit] = useState('USD');
  const [selectedWindow, setSelectedWindow] = useState('Last Year');
  const [selectedFlow, setSelectedFlow] = useState('Imports');
  const [selectedMineral, setSelectedMineral] = useState('Lithium');
  const [selectedYear, setSelectedYear] = useState('2024');
  
  // Data state for all three modes
  const [treemapData, setTreemapData] = useState({
    Import: null,
    Export: null,
    'Import Dependency': null
  });
  const [summaryStats, setSummaryStats] = useState(null);
  const [loading, setLoading] = useState({
    Import: true,
    Export: true,
    'Import Dependency': true
  });
  const [error, setError] = useState({
    Import: null,
    Export: null,
    'Import Dependency': null
  });
  
  // Refs for all three treemap containers
  const importRef = useRef();
  const exportRef = useRef();
  const dependencyRef = useRef();
  
  // Available options
  const metrics = ['Trade Value', 'Growth'];
  const units = ['USD', 'Percentage%'];
  const windows = ['Last Year', 'Last 3 years', 'Last 5 years'];
  const flows = ['Imports', 'Exports'];
  const minerals = ['Lithium', 'Cobalt', 'Nickel', 'Graphite', 'Rare Earth Elements'];
  const years = ['2024', '2023', '2022', '2021', '2020'];
  
  // Country name to ISO3 code mapping
  const countryToISO3 = {
    'China': 'CHN',
    'Australia': 'AUS',
    'Chile': 'CHL',
    'Argentina': 'ARG',
    'Brazil': 'BRA',
    'Canada': 'CAN',
    'Russia': 'RUS',
    'Russian Federation': 'RUS',
    'South Africa': 'ZAF',
    'India': 'IND',
    'Indonesia': 'IDN',
    'Malaysia': 'MYS',
    'Thailand': 'THA',
    'Philippines': 'PHL',
    'Vietnam': 'VNM',
    'Japan': 'JPN',
    'South Korea': 'KOR',
    'United States': 'USA',
    'USA': 'USA',
    'Germany': 'DEU',
    'United Kingdom': 'GBR',
    'France': 'FRA',
    'Italy': 'ITA',
    'Spain': 'ESP',
    'Netherlands': 'NLD',
    'Belgium': 'BEL',
    'Poland': 'POL',
    'Slovenia': 'SVN',
    'Sweden': 'SWE',
    'Norway': 'NOR',
    'Denmark': 'DNK',
    'Finland': 'FIN'
  };
  
  // Dropdown states
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showWindowDropdown, setShowWindowDropdown] = useState(false);
  const [showFlowDropdown, setShowFlowDropdown] = useState(false);
  const [showMineralDropdown, setShowMineralDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);


  // Fetch trade balance data for Import/Export/Import Dependency modes
  const fetchTradeBalanceData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const metricName = selectedMetric === 'Trade Value' ? 
        `Trade Value (${selectedUnit})` : 
        `Growth (${selectedUnit})`;
      
      const windowName = selectedWindow === 'Last Year' ? 'Annual (YoY)' :
                        selectedWindow === 'Last 3 years' ? '3-Year' :
                        '5-Year';
      
      // Determine flow based on mode (will be called for each mode)
      let flow = 'imports'; // Default to imports, will be overridden when called
      // For 'Import Dependency' mode, use the selected flow
      
      const params = new URLSearchParams({
        year: selectedYear,
        mineral: selectedMineral === 'All' ? '' : selectedMineral,
        flow: flow,
        window: windowName,
        metric: metricName
      });
      
      const response = await fetch(`/api/critical-minerals/trade-balance?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setTreemapData(data.data.treemapData);
        setSummaryStats(data.data.summaryStats);
      } else {
        throw new Error(data.message || 'Failed to fetch trade balance data');
      }
    } catch (err) {
      console.error('Error fetching trade balance data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  // Fetch partner risk data
  const fetchPartnerRiskData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        year: selectedYear,
        mineral: selectedMineral === 'All' ? '' : selectedMineral
      });
      
      const response = await fetch(`/api/critical-minerals/partner-risk?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Partner risk data received:', data.data);
        // Log first few entries to check data structure
        if (data.data && data.data.length > 0) {
          console.log('Sample partner risk entry:', data.data[0]);
        }
        
        // Transform partner risk data to treemap format
        const treemapData = transformPartnerRiskToTreemap(data.data);
        console.log('Transformed treemap data:', treemapData);
        setTreemapData(treemapData);
        setSummaryStats({
          totalPartners: data.data.length,
          highRisk: data.data.filter(d => d.risk_level === 'HIGH RISK').length,
          mediumRisk: data.data.filter(d => d.risk_level === 'MEDIUM RISK').length,
          lowRisk: data.data.filter(d => d.risk_level === 'LOW RISK').length
        });
      } else {
        throw new Error(data.message || 'Failed to fetch partner risk data');
      }
    } catch (error) {
      console.error('Error fetching partner risk data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Transform partner risk data to treemap format
  const transformPartnerRiskToTreemap = (partnerRiskData) => {
    console.log('🔄 Transforming partner risk data:', partnerRiskData);
    
    return {
      labels: partnerRiskData.map(d => d.country),
      parents: partnerRiskData.map(() => ''),
      values: partnerRiskData.map(d => Number(d.import_percentage) || 0.1), // Use import percentage for size
      colors: partnerRiskData.map(d => Number(d.partner_risk_score) || 0), // Use partner risk score for color
      customdata: partnerRiskData.map(d => {
        const transformed = {
          country: d.country,
          countryISO3: d.countryISO3 || countryToISO3[d.country] || d.country.substring(0, 3).toUpperCase(),
          value: Number(d.import_percentage) || 0, // Use import percentage for value (affects sorting)
          originalValue: Number(d.import_percentage) || 0, // Also store as originalValue
          riskLevel: d.risk_level || 'UNKNOWN',
          dependencyRatio: Number(d.dependency_ratio) || 0,
          importPercentage: Number(d.import_percentage) || 0,
          tradeVolume: Number(d.trade_volume) || 0,
          partnerRiskScore: Number(d.partner_risk_score) || 0 // Explicit partner risk score field
        };
        console.log(`Transformed ${d.country}:`, transformed);
        return transformed;
      })
    };
  };

  // Transform data to D3Plus format
  const transformToD3PlusFormat = (treemapData) => {
    console.log('Transforming treemap data for D3Plus:', treemapData);
    
    if (!treemapData || !treemapData.labels) {
      console.log('No treemap data available');
      return [];
    }

    const transformed = treemapData.labels.map((label, i) => ({
      id: label,
      name: label,
      value: Math.abs(treemapData.values[i] || 0), // Ensure positive values
      originalValue: treemapData.values[i],
      country: treemapData.customdata[i]?.country || label,
      ...treemapData.customdata[i]
    }));
    
    console.log('Transformed D3Plus data:', transformed);
    return transformed;
  };

  // Format values for display
  const formatValue = (value, mode) => {
    console.log('Formatting value:', value, 'Type:', typeof value, 'Mode:', mode);
    
    // Handle null, undefined, or non-numeric values
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    
    if (mode === 'Import Dependency') {
      return `${Number(value).toFixed(2)}`;
    }
    
    const metricName = selectedMetric === 'Trade Value' ? 
      `Trade Value (${selectedUnit})` : 
      `Growth (${selectedUnit})`;
      
    if (metricName === 'Trade Value (USD)') {
      return `$${Number(value).toFixed(0)}M`;
    } else if (metricName === 'Trade Value (Percentage%)') {
      return `${Number(value).toFixed(1)}%`;
    } else if (metricName === 'Growth (USD)') {
      return `$${Number(value).toFixed(0)}M`;
    } else if (metricName === 'Growth (Percentage%)') {
      return `${Number(value).toFixed(1)}%`;
    }
    return Number(value).toFixed(0);
  };

  // Optimized D3 Treemap rendering function
  const renderTreemap = useCallback((data, mode, containerRef) => {
    console.log(`🎨 Rendering ${mode} treemap:`, { data, containerRef: !!containerRef.current });
    
    if (!containerRef.current || !data || !data.children || data.children.length === 0) {
      console.log(`❌ Cannot render ${mode} treemap:`, {
        hasContainer: !!containerRef.current,
        hasData: !!data,
        hasChildren: data?.children?.length > 0
      });
      return;
    }

    // Clear previous content
    d3.select(containerRef.current).selectAll("*").remove();

    const width = 400;  // Smaller width for 3-column layout
    const height = 300; // Smaller height for compact display
    
    const svg = d3.select(containerRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .style("width", "100%")
      .style("height", "100%")
      .style("overflow", "visible");

    // Create treemap layout with minimal padding for compact design
    const treemap = d3.treemap()
      .size([width, height])
      .padding(2)
      .round(true);

    // Create hierarchy and calculate layout
    const root = d3.hierarchy(data)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value);

    treemap(root);

    // Color scale based on country region
    console.log(`🎨 Color scale for ${mode}:`, { leafCount: root.leaves().length });
    
    // Use region-based colors instead of value-based colors
    const colorScale = (data) => {
      const countryName = data.country || data.name;
      const regionColor = getRegionColor(countryName);
      console.log(`🎨 Color for ${countryName}: ${regionColor} (Region: ${getCountryRegion(countryName)})`);
      return regionColor;
    };
    
    // Create tooltip div
    const tooltip = d3.select("body").selectAll(".d3-treemap-tooltip").data([null]);
    const tooltipEnter = tooltip.enter().append("div").attr("class", "d3-treemap-tooltip");
    const tooltipUpdate = tooltipEnter.merge(tooltip)
      .style("position", "absolute")
      .style("background", isDarkMode ? "#1f2937" : "#ffffff")
      .style("color", isDarkMode ? "#ffffff" : "#1f2937")
      .style("padding", "12px")
      .style("border-radius", "8px")
      .style("font-size", "12px")
      .style("font-family", "Inter, system-ui, sans-serif")
      .style("pointer-events", "none")
      .style("z-index", "1000")
      .style("border", isDarkMode ? "1px solid #374151" : "1px solid #d1d5db")
      .style("box-shadow", "0 4px 6px -1px rgba(0, 0, 0, 0.1)")
      .style("opacity", 0);

    // Create groups for each leaf
    const leaf = svg.selectAll("g")
      .data(root.leaves())
      .enter().append("g")
      .attr("transform", d => `translate(${d.x0},${d.y0})`)
      .style("overflow", "visible");

    // Add rectangles with region-based colors
    leaf.append("rect")
      .attr("width", d => d.x1 - d.x0)
      .attr("height", d => d.y1 - d.y0)
      .attr("fill", d => colorScale(d.data))
      .attr("stroke", "#374151")
      .attr("stroke-width", 0.5)
      .attr("rx", 2)
      .style("cursor", "pointer")
      .on("mouseover", function(event, d) {
        // Highlight rectangle
        d3.select(this)
          .attr("stroke", "#ffffff")
          .attr("stroke-width", 2);
        
        // Show tooltip
        const displayName = d.data.country || d.data.name;
        const titleColor = isDarkMode ? "#ffffff" : "#1f2937";
        const labelColor = isDarkMode ? "#a3a3a3" : "#6b7280";
        let tooltipContent = `<div><strong style="color: ${titleColor}; font-size: 14px;">${displayName}</strong></div>`;
        
        if (mode === 'Import Dependency') {
          tooltipContent += `
            <div style="margin-top: 8px;">
              <div style="color: ${labelColor};">Import Dependency: <span style="color: #10b981; font-weight: bold;">${d.data.importPercentage ? Number(d.data.importPercentage).toFixed(1) : 'N/A'}%</span></div>
              <div style="color: ${labelColor};">Partner Risk Score: <span style="color: #10b981; font-weight: bold;">${d.data.partnerRiskScore ? Number(d.data.partnerRiskScore).toFixed(2) : 'N/A'}</span></div>
              <div style="color: ${labelColor};">Risk Level: <span style="color: ${d.data.riskLevel === 'HIGH RISK' ? '#ef4444' : d.data.riskLevel === 'MEDIUM RISK' ? '#f59e0b' : '#10b981'}; font-weight: bold;">${d.data.riskLevel || 'N/A'}</span></div>
            </div>
          `;
        } else {
          tooltipContent += `
            <div style="margin-top: 8px;">
              <div style="color: ${labelColor};">Trade Value: <span style="color: #3b82f6; font-weight: bold;">${formatValue(d.data.originalValue, mode)}</span></div>
              <div style="color: ${labelColor};">Growth: <span style="color: #3b82f6; font-weight: bold;">${d.data.growth ? Number(d.data.growth).toFixed(1) : 'N/A'}%</span></div>
              <div style="color: ${labelColor};">Share: <span style="color: #3b82f6; font-weight: bold;">${d.data.share ? Number(d.data.share).toFixed(1) : 'N/A'}%</span></div>
            </div>
          `;
        }
        
        tooltipUpdate
          .html(tooltipContent)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 10) + "px")
          .transition()
          .duration(200)
          .style("opacity", 1);
      })
      .on("mouseout", function() {
        // Remove highlight
        d3.select(this)
          .attr("stroke", "#374151")
          .attr("stroke-width", 0.5);
        
        // Hide tooltip
        tooltipUpdate.transition()
          .duration(300)
          .style("opacity", 0);
      });

    // Add country labels (show ISO codes for all boxes, even small ones)
    leaf.append("text")
      .attr("x", d => {
        const width = d.x1 - d.x0;
        // Center text horizontally for small boxes, left-align with padding for larger boxes
        return width < 50 ? (d.x1 - d.x0) / 2 : 6;
      })
      .attr("y", d => {
        const height = d.y1 - d.y0;
        const width = d.x1 - d.x0;
        // Center text vertically for small boxes
        if (width < 50 || height < 30) {
          // Small boxes - center vertically (dominant-baseline="middle" centers on this y value)
          return (d.y1 - d.y0) / 2;
        }
        // For larger boxes, use fixed offset from top with proper spacing
        return 20;
      })
      .attr("font-size", d => {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        const minDimension = Math.min(width, height);
        // Scale font size based on box size, ensure minimum readable size
        // Use larger minimum to ensure visibility
        if (minDimension < 15) return "10px";
        if (minDimension < 25) return "11px";
        if (minDimension < 35) return "12px";
        if (minDimension < 50) return "13px";
        if (minDimension < 60) return "14px";
        return "16px";
      })
      .attr("font-weight", "bold")
      .attr("fill", "#ffffff")
      .attr("stroke", "#000000")
      .attr("stroke-width", "0.5px")
      .attr("stroke-opacity", "0.5")
      .attr("paint-order", "stroke fill")
      .attr("font-family", "Inter, system-ui, sans-serif")
      .attr("text-anchor", d => {
        const width = d.x1 - d.x0;
        // Center text for small boxes
        return width < 50 ? "middle" : "start";
      })
      .attr("dominant-baseline", d => {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        // Use middle baseline for small boxes (centered), hanging for larger boxes (top-aligned with proper spacing)
        return (width < 50 || height < 30) ? "middle" : "hanging";
      })
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.8)")
      .style("pointer-events", "none")
      .style("opacity", "1")
      .style("visibility", "visible")
      .text(d => {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        // Prioritize ISO3 code, then fallback to country name, then data name
        const country = d.data.countryISO3 || d.data.country || d.data.name;
        
        // Always show ISO3 code if available, even for very small boxes
        if (d.data.countryISO3) {
          return d.data.countryISO3;
        }
        
        // For country names, only show if box is large enough
        if (width > 60) {
          const result = country.length > 12 ? country.substring(0, 12) + '...' : country;
          return result;
        }
        
        // For small boxes without ISO code, try to show at least first 2-3 characters
        if (width > 25 && height > 15) {
          return country.substring(0, 3).toUpperCase();
        }
        
        return "";
      });

    // Add value labels (only for rectangles large enough)
    leaf.append("text")
      .attr("x", 4)
      .attr("y", 60)
      .attr("font-size", "16px")
      .attr("fill", "#ffffff")
      .attr("font-family", "Inter, system-ui, sans-serif")
      .attr("opacity", 0.9)
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.8)")
      .text(d => {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        
        // Only show value if rectangle is big enough
        if (width > 50 && height > 50) {
          if (mode === 'Import Dependency') {
            const importPercentage = d.data.importPercentage ? Number(d.data.importPercentage).toFixed(1) : '0.0';
            return `${importPercentage}%`;
          } else {
            return formatValue(d.data.originalValue, mode);
          }
        }
        return "";
      });

    // Add Partner Risk Score labels for Import Dependency mode (second line)
    leaf.append("text")
      .attr("x", 4)
      .attr("y", 80)
      .attr("font-size", "16px")
      .attr("fill", "#ffffff")
      .attr("font-family", "Inter, system-ui, sans-serif")
      .attr("opacity", 0.9)
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.8)")
      .text(d => {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        
        // Only show risk score if rectangle is big enough and it's Import Dependency mode
        if (mode === 'Import Dependency' && width > 50 && height > 60) {
          const riskScore = d.data.partnerRiskScore ? Number(d.data.partnerRiskScore).toFixed(1) : '0.0';
          return `Risk: ${riskScore}`;
        }
        return "";
      });
  }, [selectedMetric, selectedUnit, formatValue, getRegionColor, getCountryRegion, isDarkMode]);

  // Transform data to D3 hierarchical format
  const transformToD3Format = (treemapData) => {
    console.log('Transforming treemap data for D3:', treemapData);
    
    if (!treemapData || !treemapData.labels) {
      console.log('No treemap data available');
      return { name: "root", children: [] };
    }

    const children = treemapData.labels.map((label, i) => {
      const country = treemapData.customdata[i]?.country || label;
      const iso3Code = countryToISO3[country] || country.substring(0, 3).toUpperCase();
      const value = Math.abs(treemapData.values[i] || 0);
      console.log(`🌍 Transforming ${country} -> ISO3: ${iso3Code}, Value: ${value}`);
      return {
        name: label,
        value: value, // Ensure positive values - this is used for sorting
        originalValue: treemapData.values[i],
        country: country,
        countryISO3: iso3Code,
        ...treemapData.customdata[i]
      };
    });
    
    const transformed = {
      name: "root",
      children: children
    };
    
    console.log('Transformed D3 data:', transformed);
    console.log('Children values:', children.map(c => ({ name: c.name, value: c.value })));
    return transformed;
  };

  // Render all treemaps when data changes
  useEffect(() => {
    modes.forEach(mode => {
      console.log(`🎯 Checking ${mode} mode:`, {
        hasData: !!treemapData[mode],
        isLoading: loading[mode],
        dataType: typeof treemapData[mode],
        dataContent: treemapData[mode]
      });
      
      if (treemapData[mode] && !loading[mode]) {
        let d3Data;
        
        if (mode === 'Import Dependency') {
          // Partner risk data comes in a different format
          if (Array.isArray(treemapData[mode])) {
            // If it's already an array (from transformPartnerRiskToTreemap)
            d3Data = transformToD3Format({ name: "root", children: treemapData[mode] });
          } else if (treemapData[mode].labels) {
            // If it's in the treemap format with labels/values
            d3Data = transformToD3Format(treemapData[mode]);
          } else {
            console.error('❌ Unexpected Import Dependency data format:', treemapData[mode]);
            return;
          }
        } else {
          // Regular trade balance data
          d3Data = transformToD3Format(treemapData[mode]);
        }
        
        const containerRef = mode === 'Import' ? importRef : 
                           mode === 'Export' ? exportRef : dependencyRef;
        
        console.log(`🎨 Rendering ${mode} treemap with data:`, d3Data);
        renderTreemap(d3Data, mode, containerRef);
      }
    });
  }, [treemapData, loading, renderTreemap]);



  // Fetch real data for all modes
  const fetchAllModesData = async () => {
    console.log('Fetching real data for all modes...');
    
    const newLoading = { Import: true, Export: true, 'Import Dependency': true };
    const newError = { Import: null, Export: null, 'Import Dependency': null };
    const newData = { Import: null, Export: null, 'Import Dependency': null };
    
    setLoading(newLoading);
    setError(newError);
    
    // Fetch Import data
    try {
      const importData = await fetchTradeBalanceDataForMode('imports');
      newData.Import = importData;
      newLoading.Import = false;
    } catch (err) {
      console.error('Error fetching import data:', err);
      newError.Import = err.message;
      newLoading.Import = false;
    }
    
    // Fetch Export data
    try {
      const exportData = await fetchTradeBalanceDataForMode('exports');
      newData.Export = exportData;
      newLoading.Export = false;
    } catch (err) {
      console.error('Error fetching export data:', err);
      newError.Export = err.message;
      newLoading.Export = false;
    }
    
    // Fetch Import Dependency data
    try {
      const dependencyData = await fetchPartnerRiskDataForMode();
      console.log('✅ Dependency data received:', dependencyData);
      newData['Import Dependency'] = dependencyData;
      newLoading['Import Dependency'] = false;
    } catch (err) {
      console.error('Error fetching dependency data:', err);
      newError['Import Dependency'] = err.message;
      newLoading['Import Dependency'] = false;
    }
    
    setTreemapData(newData);
    setLoading(newLoading);
    setError(newError);
  };

  // Load data on component mount
  useEffect(() => {
    fetchAllModesData();
  }, []);

  // Fetch trade balance data for specific mode
  const fetchTradeBalanceDataForMode = async (flow) => {
    const metricName = selectedMetric === 'Trade Value' ? 
      `Trade Value (${selectedUnit})` : 
      `Growth (${selectedUnit})`;
    
    const windowName = selectedWindow === 'Last Year' ? 'Annual (YoY)' :
                      selectedWindow === 'Last 3 years' ? '3-Year' :
                      '5-Year';
    
    const params = new URLSearchParams({
      year: selectedYear,
      mineral: selectedMineral === 'All' ? '' : selectedMineral,
      flow: flow,
      window: windowName,
      metric: metricName
    });
    
    const response = await fetch(`/api/critical-minerals/trade-balance?${params}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.data) {
      return data.data.treemapData;
    } else {
      throw new Error(data.message || 'Failed to fetch trade balance data');
    }
  };

  // Fetch partner risk data for Import Dependency mode
  const fetchPartnerRiskDataForMode = async () => {
    const params = new URLSearchParams({
      year: selectedYear,
      mineral: selectedMineral === 'All' ? '' : selectedMineral
    });
    
    const response = await fetch(`/api/critical-minerals/partner-risk?${params}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success) {
      // Check if there's no data for 2024
      if (selectedYear === '2024' && (!data.data || data.data.length === 0)) {
        throw new Error('No data available for this year');
      }
      return transformPartnerRiskToTreemap(data.data);
    } else {
      throw new Error(data.message || 'Failed to fetch partner risk data');
    }
  };

  // Fetch data when parameters change (now fetches for all modes)
  useEffect(() => {
    console.log('Parameters changed, fetching data for all modes...');
    fetchAllModesData();
  }, [selectedMetric, selectedUnit, selectedWindow, selectedFlow, selectedMineral, selectedYear]);

  // Handle clicking outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowModeDropdown(false);
        setShowMetricDropdown(false);
        setShowUnitDropdown(false);
        setShowWindowDropdown(false);
        setShowFlowDropdown(false);
        setShowMineralDropdown(false);
        setShowYearDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);




  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-200">
      {/* Header */}
    

      {/* Horizontal Controls */}
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-6 transition-colors duration-200">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Selection Criteria</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Metric Selection */}
          <div>
            <label className="block text-xs text-gray-700 dark:text-gray-300 mb-2">Metric</label>
            <div className="relative">
              <button
                onClick={() => setShowMetricDropdown(!showMetricDropdown)}
                className="w-full flex items-center justify-between bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 text-gray-900 dark:text-white px-3 py-2 rounded border border-gray-300 dark:border-gray-500 text-sm transition-colors duration-200"
              >
                <span>{selectedMetric}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showMetricDropdown && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded shadow-lg z-10">
                  {metrics.map((metric) => (
                    <button
                      key={metric}
                      onClick={() => {
                        setSelectedMetric(metric);
                        setShowMetricDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-500 first:rounded-t last:rounded-b transition-colors duration-200"
                    >
                      {metric}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Unit Selection */}
          <div>
            <label className="block text-xs text-gray-700 dark:text-gray-300 mb-2">Unit</label>
            <div className="relative">
              <button
                onClick={() => setShowUnitDropdown(!showUnitDropdown)}
                className="w-full flex items-center justify-between bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 text-gray-900 dark:text-white px-3 py-2 rounded border border-gray-300 dark:border-gray-500 text-sm transition-colors duration-200"
              >
                <span>{selectedUnit}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showUnitDropdown && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded shadow-lg z-10">
                  {units.map((unit) => (
                    <button
                      key={unit}
                      onClick={() => {
                        setSelectedUnit(unit);
                        setShowUnitDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-500 first:rounded-t last:rounded-b transition-colors duration-200"
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mineral Selection */}
          <div>
            <label className="block text-xs text-gray-700 dark:text-gray-300 mb-2">Mineral</label>
            <div className="relative">
              <button
                onClick={() => setShowMineralDropdown(!showMineralDropdown)}
                className="w-full flex items-center justify-between bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 text-gray-900 dark:text-white px-3 py-2 rounded border border-gray-300 dark:border-gray-500 text-sm transition-colors duration-200"
              >
                <span>{selectedMineral}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showMineralDropdown && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded shadow-lg z-10">
                  {minerals.map((mineral) => (
                    <button
                      key={mineral}
                      onClick={() => {
                        setSelectedMineral(mineral);
                        setShowMineralDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-500 first:rounded-t last:rounded-b transition-colors duration-200"
                    >
                      {mineral}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Year Selection */}
          <div>
            <label className="block text-xs text-gray-700 dark:text-gray-300 mb-2">Year</label>
            <div className="relative">
              <button
                onClick={() => setShowYearDropdown(!showYearDropdown)}
                className="w-full flex items-center justify-between bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 text-gray-900 dark:text-white px-3 py-2 rounded border border-gray-300 dark:border-gray-500 text-sm transition-colors duration-200"
              >
                <span>{selectedYear}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showYearDropdown && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded shadow-lg z-10 max-h-48 overflow-y-auto">
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => {
                        setSelectedYear(year);
                        setShowYearDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-500 first:rounded-t last:rounded-b transition-colors duration-200"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      

      {/* Country Grouping Legend */}
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-6 transition-colors duration-200">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Country Grouping Legend</h3>
        <div className="flex flex-wrap items-center gap-4">
          {Object.entries(regionColors).map(([region, color]) => (
            <div key={region} className="flex items-center gap-2">
              <div 
                className="w-6 h-6 rounded border border-gray-300 dark:border-gray-500"
                style={{ backgroundColor: color }}
              ></div>
              <span className="text-sm text-gray-700 dark:text-gray-300">{region}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Three Treemaps Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {modes.map((mode) => (
          <div key={mode} className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
            {/* Individual Treemap Title */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
                {mode} - {selectedMineral} - {selectedYear}
              </h3>
            </div>
            
            {/* Individual Treemap Container */}
            <div className="h-80 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-200 dark:bg-gray-700 transition-colors duration-200">
              {loading[mode] ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-500 mx-auto mb-2"></div>
                    <div className="text-gray-600 dark:text-gray-300 text-sm">Loading {mode.toLowerCase()}...</div>
                  </div>
                </div>
              ) : error[mode] ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-red-600 dark:text-red-400">
                    <div className="text-lg mb-2">⚠️</div>
                    <div className="text-sm">Error loading {mode.toLowerCase()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{error[mode]}</div>
                  </div>
                </div>
              ) : mode === 'Import Dependency' && selectedYear === '2024' ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    
                    <div className="text-sm font-medium">Data not available for this year</div>
                    <div className="text-xs text-gray-600 dark:text-gray-500 mt-1">Import Dependency data for {selectedMineral} in {selectedYear}</div>
                  </div>
                </div>
              ) : treemapData[mode] ? (
                <svg 
                  ref={mode === 'Import' ? importRef : mode === 'Export' ? exportRef : dependencyRef} 
                  className="w-full h-full"
                ></svg>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <div className="text-lg mb-2">📊</div>
                    <div className="text-sm">No {mode.toLowerCase()} data</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Source: UN Comtrade</p>
          </div>
        </div>
      </div>

      {/* Calculation Notes */}
      {/* <div className="text-xs text-gray-600 dark:text-gray-400 mt-6">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Notes on Calculations</h4>
          
          <div className="space-y-2">
            <p>
              <strong>Annual (YoY) Change:</strong> Compares the current year's value with the previous year (Year <em>t</em> vs. Year <em>t</em>-1).
            </p>
            
            <p>
              <strong>3-Year Change:</strong> Compares the current year's value with that of three years earlier (Year <em>t</em> vs. Year <em>t</em>-3).
            </p>
            
            <p>
              <strong>5-Year Change:</strong> Compares the current year's value with that of five years earlier (Year <em>t</em> vs. Year <em>t</em>-5).
            </p>
            
            <p>
              <strong>Growth (USD):</strong> The absolute difference between the current and reference year values.
              <br />
              <strong>Formula:</strong> <code className="bg-gray-200 dark:bg-gray-800 px-1 rounded transition-colors duration-200">Growth (USD) = Value_now - Value_prev</code>
            </p>
            
            <p>
              <strong>Growth (%):</strong> The percentage change relative to the reference year.
              <br />
              <strong>Formula:</strong> <code className="bg-gray-200 dark:bg-gray-800 px-1 rounded transition-colors duration-200">Growth (%) = ((Value_now / Value_prev) - 1) × 100</code>
            </p>
            
            <p>
              <strong>Share (%):</strong> The contribution of a country's imports or exports of a mineral relative to the total trade of that mineral in the current year.
              <br />
              <strong>Formula:</strong> <code className="bg-gray-200 dark:bg-gray-800 px-1 rounded transition-colors duration-200">Share (%) = (Value_now / Total trade of the mineral in that year) × 100</code>
            </p>
          </div>
      </div> */}
    </div>
  );
};

export default TradeBalanceTreemap;
